import { supabase } from './supabase';

/**
 * Get the current MFA assurance level for the active session.
 * Returns { currentLevel, nextLevel } where levels are 'aal1' or 'aal2'.
 */
export async function getMfaStatus() {
    const { data, error } =
        await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) throw error;
    return data;
}

/**
 * Get all verified TOTP factors for the current user.
 */
export async function getVerifiedFactors() {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    return (data.totp ?? []).filter(f => f.status === 'verified');
}

/**
 * Enroll a new TOTP factor. Returns the factor ID, QR code, and secret.
 */
export async function enrollTotp(friendlyName?: string) {
    const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        issuer: 'hailhelper.app',
        ...(friendlyName ? { friendlyName } : {}),
    });
    if (error) throw error;
    return data;
}

/**
 * Verify a TOTP code against an enrolled factor.
 * On success, promotes the session to aal2.
 */
export async function verifyTotp(factorId: string, code: string) {
    const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code,
    });
    if (error) throw error;
    return data;
}

/**
 * Remove an enrolled MFA factor.
 */
export async function unenrollFactor(factorId: string) {
    const { data, error } = await supabase.auth.mfa.unenroll({
        factorId,
    });
    if (error) throw error;
    return data;
}

/**
 * Clean up any unverified TOTP factors (orphaned enrollments).
 * Call this before starting a fresh enrollment to prevent
 * hitting the 10-factor limit.
 */
export async function cleanupUnverifiedFactors() {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    const unverified = (data.all ?? []).filter(
        f => f.factor_type === 'totp' && f.status !== 'verified'
    );
    for (const factor of unverified) {
        await supabase.auth.mfa.unenroll({ factorId: factor.id });
    }
}
